import type { Command, CommandContext, CommandResult } from "@/commands/Command";
import {
  collectScriptIdsInEntry,
  createDocumentScript,
  findDocumentScript,
  findScriptEntry,
  findScriptEntryPath,
  findScriptFolder,
  isScriptEntryDescendant,
  parentEntriesForFolder,
  uniqueScriptName,
  type DocumentScript,
  type ScriptEntry,
  type ScriptFolder,
  type ScriptLibrary,
} from "@/document/model/documentScripts";
import { findSceneNode, getAllSceneNodes } from "@/document/model/document";

function ensureScriptLibrary(context: CommandContext): ScriptLibrary {
  if (!context.document.scripts) {
    context.document.scripts = { items: [] };
  }
  return context.document.scripts;
}

function cloneLibrary(library: ScriptLibrary): ScriptLibrary {
  return structuredClone(library);
}

function detachScriptIds(context: CommandContext, scriptIds: ReadonlySet<string>): string[] {
  const detachedFrameIds: string[] = [];
  for (const node of getAllSceneNodes(context.document)) {
    if (node.type === "frame" && node.scriptId && scriptIds.has(node.scriptId)) {
      detachedFrameIds.push(node.id);
      delete node.scriptId;
    }
  }
  return detachedFrameIds;
}

export class CreateDocumentScriptCommand implements Command {
  readonly id = "script.create";
  readonly label = "Create Script";

  constructor(
    private readonly script: DocumentScript,
    private readonly parentFolderId?: string,
    private readonly index?: number,
  ) {}

  execute(context: CommandContext): CommandResult {
    const library = ensureScriptLibrary(context);
    const parent = parentEntriesForFolder(library, this.parentFolderId);
    const insertionIndex = this.index ?? parent.length;
    parent.splice(insertionIndex, 0, structuredClone(this.script));
    return { changedNodeIds: [], affectedBounds: [] };
  }

  undo(context: CommandContext): CommandResult {
    const library = ensureScriptLibrary(context);
    const path = findScriptEntryPath(library, this.script.id);
    if (path) {
      path.parent.splice(path.index, 1);
    }
    return { changedNodeIds: [], affectedBounds: [] };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class CreateScriptFolderCommand implements Command {
  readonly id = "script.createFolder";
  readonly label = "Create Script Folder";

  constructor(
    private readonly folder: ScriptFolder,
    private readonly parentFolderId?: string,
    private readonly index?: number,
  ) {}

  execute(context: CommandContext): CommandResult {
    const library = ensureScriptLibrary(context);
    const parent = parentEntriesForFolder(library, this.parentFolderId);
    const insertionIndex = this.index ?? parent.length;
    parent.splice(insertionIndex, 0, structuredClone(this.folder));
    return { changedNodeIds: [], affectedBounds: [] };
  }

  undo(context: CommandContext): CommandResult {
    const library = ensureScriptLibrary(context);
    const path = findScriptEntryPath(library, this.folder.id);
    if (path) {
      path.parent.splice(path.index, 1);
    }
    return { changedNodeIds: [], affectedBounds: [] };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class RenameScriptEntryCommand implements Command {
  readonly id = "script.renameEntry";
  readonly label = "Rename Script Entry";

  constructor(
    private readonly entryId: string,
    private readonly fromName: string,
    private readonly toName: string,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.apply(context, this.toName);
  }

  undo(context: CommandContext): CommandResult {
    return this.apply(context, this.fromName);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private apply(context: CommandContext, name: string): CommandResult {
    const entry = findScriptEntry(ensureScriptLibrary(context), this.entryId);
    if (!entry) {
      throw new Error(`Script entry ${this.entryId} was not found.`);
    }
    entry.name = name;
    return { changedNodeIds: [], affectedBounds: [] };
  }
}

/** @deprecated Prefer RenameScriptEntryCommand */
export class RenameDocumentScriptCommand extends RenameScriptEntryCommand {
  readonly id = "script.rename";
  readonly label = "Rename Script";
}

export class UpdateDocumentScriptSourceCommand implements Command {
  readonly id = "script.updateSource";
  readonly label = "Edit Script";

  constructor(
    private readonly scriptId: string,
    private readonly fromSource: string,
    private readonly toSource: string,
  ) {}

  execute(context: CommandContext): CommandResult {
    return this.apply(context, this.toSource);
  }

  undo(context: CommandContext): CommandResult {
    return this.apply(context, this.fromSource);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof UpdateDocumentScriptSourceCommand && command.scriptId === this.scriptId;
  }

  merge(command: Command): Command {
    if (!(command instanceof UpdateDocumentScriptSourceCommand)) {
      return this;
    }
    return new UpdateDocumentScriptSourceCommand(this.scriptId, this.fromSource, command.toSource);
  }

  private apply(context: CommandContext, source: string): CommandResult {
    const script = findDocumentScript(ensureScriptLibrary(context), this.scriptId);
    if (!script) {
      throw new Error(`Script ${this.scriptId} was not found.`);
    }
    script.source = source;
    return { changedNodeIds: [], affectedBounds: [] };
  }
}

export class DuplicateDocumentScriptCommand implements Command {
  readonly id = "script.duplicate";
  readonly label = "Duplicate Script";
  private created?: DocumentScript;
  private insertIndex = 0;
  private parentFolderId?: string;

  constructor(private readonly scriptId: string) {}

  execute(context: CommandContext): CommandResult {
    const library = ensureScriptLibrary(context);
    if (!this.created) {
      const source = findDocumentScript(library, this.scriptId);
      if (!source) {
        throw new Error(`Script ${this.scriptId} was not found.`);
      }
      const path = findScriptEntryPath(library, this.scriptId);
      this.created = createDocumentScript(
        uniqueScriptName(library, `${source.name} Copy`),
        source.source,
      );
      this.insertIndex = path ? path.index + 1 : library.items.length;
      this.parentFolderId = path && path.parent !== library.items
        ? findFolderIdForChildren(library, path.parent)
        : undefined;
    }

    const parent = parentEntriesForFolder(library, this.parentFolderId);
    parent.splice(Math.min(this.insertIndex, parent.length), 0, structuredClone(this.created));
    return { changedNodeIds: [], affectedBounds: [] };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.created) {
      return { changedNodeIds: [], affectedBounds: [] };
    }
    const library = ensureScriptLibrary(context);
    const path = findScriptEntryPath(library, this.created.id);
    if (path) {
      path.parent.splice(path.index, 1);
    }
    return { changedNodeIds: [], affectedBounds: [] };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  getCreatedScriptId(): string | undefined {
    return this.created?.id;
  }
}

function findFolderIdForChildren(library: ScriptLibrary, children: ScriptEntry[]): string | undefined {
  if (library.items === children) {
    return undefined;
  }
  for (const folder of walkFolders(library.items)) {
    if (folder.children === children) {
      return folder.id;
    }
  }
  return undefined;
}

function walkFolders(entries: ScriptEntry[]): ScriptFolder[] {
  const out: ScriptFolder[] = [];
  for (const entry of entries) {
    if (entry.type === "folder") {
      out.push(entry, ...walkFolders(entry.children));
    }
  }
  return out;
}

export class DeleteScriptEntryCommand implements Command {
  readonly id = "script.deleteEntry";
  readonly label = "Delete Script Entry";
  private beforeLibrary?: ScriptLibrary;
  private detachedFrameIds: string[] = [];

  constructor(private readonly entryId: string) {}

  execute(context: CommandContext): CommandResult {
    const library = ensureScriptLibrary(context);
    const path = findScriptEntryPath(library, this.entryId);
    if (!path) {
      throw new Error(`Script entry ${this.entryId} was not found.`);
    }
    this.beforeLibrary = cloneLibrary(library);
    const scriptIds = new Set(collectScriptIdsInEntry(path.entry));
    this.detachedFrameIds = detachScriptIds(context, scriptIds);
    path.parent.splice(path.index, 1);
    return {
      changedNodeIds: this.detachedFrameIds,
      affectedBounds: [],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeLibrary) {
      throw new Error("Cannot undo script delete; snapshot missing.");
    }
    context.document.scripts = cloneLibrary(this.beforeLibrary);
    return {
      changedNodeIds: this.detachedFrameIds,
      affectedBounds: [],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

/** @deprecated Prefer DeleteScriptEntryCommand */
export class DeleteDocumentScriptCommand extends DeleteScriptEntryCommand {
  readonly id = "script.delete";
  readonly label = "Delete Script";
}

export class MoveScriptEntryCommand implements Command {
  readonly id = "script.moveEntry";
  readonly label = "Move Script Entry";
  private beforeLibrary?: ScriptLibrary;

  constructor(
    private readonly entryId: string,
    private readonly toParentFolderId: string | undefined,
    private readonly toIndex: number,
  ) {}

  execute(context: CommandContext): CommandResult {
    const library = ensureScriptLibrary(context);
    this.beforeLibrary = cloneLibrary(library);

    if (this.toParentFolderId) {
      if (this.toParentFolderId === this.entryId) {
        throw new Error("Cannot move a folder into itself.");
      }
      if (isScriptEntryDescendant(library, this.entryId, this.toParentFolderId)) {
        throw new Error("Cannot move a folder into its descendant.");
      }
      if (!findScriptFolder(library, this.toParentFolderId)) {
        throw new Error(`Folder ${this.toParentFolderId} was not found.`);
      }
    }

    const fromPath = findScriptEntryPath(library, this.entryId);
    if (!fromPath) {
      throw new Error(`Script entry ${this.entryId} was not found.`);
    }

    const [entry] = fromPath.parent.splice(fromPath.index, 1);
    if (!entry) {
      throw new Error(`Script entry ${this.entryId} was not found.`);
    }

    const destination = parentEntriesForFolder(library, this.toParentFolderId);
    let insertIndex = Math.max(0, Math.min(this.toIndex, destination.length));

    // Adjust index when moving within the same parent after removal.
    if (fromPath.parent === destination && fromPath.index < insertIndex) {
      insertIndex -= 1;
    }

    destination.splice(insertIndex, 0, entry);
    return { changedNodeIds: [], affectedBounds: [] };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeLibrary) {
      throw new Error("Cannot undo script move; snapshot missing.");
    }
    context.document.scripts = cloneLibrary(this.beforeLibrary);
    return { changedNodeIds: [], affectedBounds: [] };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class ImportScriptsFolderCommand implements Command {
  readonly id = "script.importFolder";
  readonly label = "Import Scripts";
  private beforeLibrary?: ScriptLibrary;

  constructor(private readonly folder: ScriptFolder) {}

  execute(context: CommandContext): CommandResult {
    const library = ensureScriptLibrary(context);
    this.beforeLibrary = cloneLibrary(library);
    library.items.push(structuredClone(this.folder));
    return { changedNodeIds: [], affectedBounds: [] };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeLibrary) {
      throw new Error("Cannot undo script import; snapshot missing.");
    }
    context.document.scripts = cloneLibrary(this.beforeLibrary);
    return { changedNodeIds: [], affectedBounds: [] };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class ReplaceScriptLibraryCommand implements Command {
  readonly id = "script.replaceLibrary";
  readonly label = "Replace Scripts";
  private beforeLibrary?: ScriptLibrary;
  private detachedFrameIds: string[] = [];

  constructor(private readonly nextLibrary: ScriptLibrary) {}

  execute(context: CommandContext): CommandResult {
    const library = ensureScriptLibrary(context);
    this.beforeLibrary = cloneLibrary(library);
    const nextIds = new Set(
      this.nextLibrary.items.flatMap(entry => collectScriptIdsInEntry(entry)),
    );
    const previousIds = new Set(
      library.items.flatMap(entry => collectScriptIdsInEntry(entry)),
    );
    const removedIds = new Set([...previousIds].filter(id => !nextIds.has(id)));
    this.detachedFrameIds = detachScriptIds(context, removedIds);
    context.document.scripts = cloneLibrary(this.nextLibrary);
    return {
      changedNodeIds: this.detachedFrameIds,
      affectedBounds: [],
    };
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforeLibrary) {
      throw new Error("Cannot undo script replace; snapshot missing.");
    }
    context.document.scripts = cloneLibrary(this.beforeLibrary);
    return {
      changedNodeIds: this.detachedFrameIds,
      affectedBounds: [],
    };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }
}

export class SetFrameScriptIdCommand implements Command {
  readonly id = "frame.setScriptId";
  readonly label: string;

  constructor(
    private readonly frameId: string,
    private readonly fromScriptId: string | undefined,
    private readonly toScriptId: string | undefined,
  ) {
    if (fromScriptId === undefined && toScriptId !== undefined) {
      this.label = "Attach Script";
    } else if (fromScriptId !== undefined && toScriptId === undefined) {
      this.label = "Detach Script";
    } else {
      this.label = "Change Script";
    }
  }

  execute(context: CommandContext): CommandResult {
    return this.apply(context, this.toScriptId);
  }

  undo(context: CommandContext): CommandResult {
    return this.apply(context, this.fromScriptId);
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  private apply(context: CommandContext, scriptId: string | undefined): CommandResult {
    const node = findSceneNode(context.document, this.frameId);
    if (!node || node.type !== "frame") {
      throw new Error(`Node ${this.frameId} is not a frame.`);
    }
    if (scriptId !== undefined && !findDocumentScript(ensureScriptLibrary(context), scriptId)) {
      throw new Error(`Script ${scriptId} was not found.`);
    }
    if (scriptId === undefined) {
      delete node.scriptId;
    } else {
      node.scriptId = scriptId;
    }
    return {
      changedNodeIds: [this.frameId],
      affectedBounds: [node.bounds],
    };
  }
}
